import React, { useCallback, useRef, useEffect } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import clsx from 'clsx';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';

import DroppableTypes from '../../constants/DroppableTypes';
import Paths from '../../constants/Paths';
import { useSidebar } from '../../contexts/SidebarContext';
import BoardActionsPopup from '../BoardActionsPopup';
import BoardAddPopup from '../BoardAddPopup';
import ConnectionsPopup from '../ConnectionsPopup';
import Filter from '../Filter';
import ProjectActionsPopup from '../ProjectActionsPopup';
import ProjectAddPopup from '../ProjectAddPopup';
import { Button, ButtonVariant, Icon, IconType, IconSize } from '../Utils';

import * as gs from '../../global.module.scss';
import * as s from './Sidebar.module.scss';

const Sidebar = React.memo(
  ({
    children,
    settingsOnly,
    path,
    projects,
    filteredProjects,
    managedProjects,
    currProjectId,
    currBoardId,
    isAdmin,
    canAddProject,
    defaultData,
    isSubmitting,
    filterQuery,
    filterTarget,
    sidebarCompact,
    instanceNotificationCount,
    usersNotificationCount,
    boardTemplates,
    mailServiceAvailable,
    mailServiceInboundEmail,
    onProjectCreate,
    onProjectUpdate,
    onBoardCreate,
    onBoardUpdate,
    onBoardMove,
    onBoardDelete,
    onBoardExport,
    onBoardFetch,
    onBoardTemplateCreate,
    onBoardTemplateUpdate,
    onBoardTemplateDelete,
    onChangeFilterQuery,
    onProjectMembershipUpdate,
    onActivitiesProjectFetch,
    onActivitiesBoardFetch,
    onMailTokenCreate,
    onMailTokenUpdate,
    onMailTokenDelete,
    onBoardMembershipUpdate,
  }) => {
    const [t] = useTranslation();
    const { sidebarShown, toggleSidebar } = useSidebar();
    const projectRefs = useRef({});
    const boardRefs = useRef({});
    const isFilteringBoards = filterTarget === 'board' && !!filterQuery;

    const handleToggleProjectCollapse = useCallback(
      (project) => {
        onProjectMembershipUpdate(project.id, { isCollapsed: !project.isCollapsed });
      },
      [onProjectMembershipUpdate],
    );

    const handleDragEnd = useCallback(
      ({ draggableId, source, destination }) => {
        if (!destination || source.index === destination.index) {
          return;
        }

        onBoardMove(draggableId, destination.index);
      },
      [onBoardMove],
    );

    const scrollItemIntoView = useCallback((itemRef) => {
      if (itemRef) {
        const rect = itemRef.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

        if (!isVisible) {
          itemRef.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      }
    }, []);

    const handleFilterQueryClear = useCallback(() => {
      const timeout = setTimeout(() => {
        if (currBoardId) {
          scrollItemIntoView(boardRefs.current[currBoardId]);
        } else if (currProjectId) {
          scrollItemIntoView(projectRefs.current[currProjectId]);
        }
      }, 0);
      return () => clearTimeout(timeout);
    }, [currBoardId, currProjectId, scrollItemIntoView]);

    useEffect(() => {
      if (currBoardId) {
        scrollItemIntoView(boardRefs.current[currBoardId]);
      } else if (currProjectId) {
        scrollItemIntoView(projectRefs.current[currProjectId]);
      }
    }, [currProjectId, currBoardId, scrollItemIntoView]);

    const projectsNode = filteredProjects.map((project) => {
      const isProjectManager = managedProjects.some((p) => p.id === project.id);
      return (
        <div key={project.id}>
          {/* eslint-disable-next-line no-return-assign */}
          <div className={clsx(s.sidebarItemProject, !currBoardId && currProjectId === project.id && s.sidebarItemActive)} ref={(el) => (projectRefs.current[project.id] = el)}>
            <Button variant={ButtonVariant.Icon} title={project.isCollapsed ? t('common.showBoards') : t('common.hideBoards')} className={s.sidebarButton} onClick={() => handleToggleProjectCollapse(project)}>
              <Icon type={IconType.TriangleDown} size={IconSize.Size8} className={clsx(s.collapseIcon, project.isCollapsed && s.collapseIconCollapsed)} />
            </Button>
            <Link to={Paths.PROJECTS.replace(':id', project.id)} className={s.sidebarItemInner}>
              <Button variant={ButtonVariant.NoBackground} content={project.name} className={clsx(s.sidebarButton, s.sidebarButtonPadding)} />
            </Link>
            {project.notificationsTotal > 0 && <span className={s.notification}>{project.notificationsTotal}</span>}
            <ProjectActionsPopup
              activities={project.activities}
              isActivitiesFetching={project.isActivitiesFetching}
              isAllActivitiesFetched={project.isAllActivitiesFetched}
              lastActivityId={project.lastActivityId}
              name={project.name}
              projectId={project.id}
              managedProjects={managedProjects}
              defaultDataRename={pick(project, 'name')}
              isAdmin={isAdmin}
              createdAt={project.createdAt}
              createdBy={project.createdBy}
              updatedAt={project.updatedAt}
              updatedBy={project.updatedBy}
              memberships={project.memberships}
              templates={boardTemplates}
              isProjectManager={isProjectManager}
              onUpdate={(data) => onProjectUpdate(project.id, data)}
              onBoardCreate={onBoardCreate}
              onTemplateUpdate={onBoardTemplateUpdate}
              onTemplateDelete={onBoardTemplateDelete}
              onActivitiesFetch={() => onActivitiesProjectFetch(project.id)}
              position="right-start"
              offset={10}
              hideCloseButton
            >
              <Button variant={ButtonVariant.Icon} title={t('common.editProject', { context: 'title' })} className={clsx(s.sidebarButton, s.hoverButton)}>
                <Icon type={IconType.EllipsisVertical} size={IconSize.Size13} />
              </Button>
            </ProjectActionsPopup>
          </div>
          {(!project.isCollapsed || isFilteringBoards || currProjectId === project.id) && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="boards" type={DroppableTypes.BOARD} direction="vertical">
                {({ innerRef, droppableProps, placeholder }) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <div {...droppableProps} ref={innerRef}>
                    {project.boards.map((board, index) => (
                      <Draggable key={board.id} draggableId={board.id} index={index} isDragDisabled={!board.isPersisted || !isProjectManager}>
                        {/* eslint-disable-next-line no-shadow */}
                        {({ innerRef, draggableProps, dragHandleProps }) => (
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          <div {...draggableProps} ref={innerRef} className={s.boardDraggable}>
                            {board.isPersisted && (
                              <div
                                key={board.id}
                                className={clsx(s.sidebarItemBoard, currBoardId === board.id && s.sidebarItemActive)}
                                // eslint-disable-next-line no-return-assign
                                ref={(el) => (boardRefs.current[board.id] = el)}
                              >
                                {isProjectManager && (
                                  // eslint-disable-next-line react/jsx-props-no-spreading
                                  <div {...dragHandleProps}>
                                    <Button variant={ButtonVariant.Icon} title={t('common.reorderBoards')} className={clsx(s.reorderBoardsButton, s.hoverButton)}>
                                      <Icon type={IconType.MoveUpDown} size={IconSize.Size13} />
                                    </Button>
                                  </div>
                                )}
                                <Link to={Paths.BOARDS.replace(':id', board.id)} className={clsx(s.sidebarItemInner, !isProjectManager && s.boardCannotManage)}>
                                  <Button variant={ButtonVariant.NoBackground} content={board.name} className={clsx(s.boardButton, s.sidebarButton)} />
                                </Link>
                                {board.isGithubConnected &&
                                  (isProjectManager ? (
                                    <ConnectionsPopup defaultData={pick(board, ['isGithubConnected', 'githubRepo'])} onUpdate={(data) => onBoardUpdate(board.id, data)} offset={30} position="right-start">
                                      <Icon
                                        type={IconType.GitHub}
                                        size={IconSize.Size13}
                                        className={clsx(s.github, board.notificationsTotal > 0 && s.githubNotifications)}
                                        title={t('common.connectedToGithub', { repo: board.githubRepo })}
                                      />
                                    </ConnectionsPopup>
                                  ) : (
                                    <div>
                                      <Icon
                                        type={IconType.GitHub}
                                        size={IconSize.Size13}
                                        className={clsx(s.github, board.notificationsTotal > 0 && s.githubNotifications)}
                                        title={t('common.connectedToGithub', { repo: board.githubRepo })}
                                      />
                                    </div>
                                  ))}
                                {board.notificationsTotal > 0 && <span className={s.notification}>{board.notificationsTotal}</span>}
                                <BoardActionsPopup
                                  activities={board.activities}
                                  isActivitiesFetching={board.isActivitiesFetching}
                                  isAllActivitiesFetched={board.isAllActivitiesFetched}
                                  lastActivityId={board.lastActivityId}
                                  defaultDataRename={pick(board, 'name')}
                                  defaultDataGithub={pick(board, ['isGithubConnected', 'githubRepo'])}
                                  createdAt={board.createdAt}
                                  createdBy={board.createdBy}
                                  updatedAt={board.updatedAt}
                                  updatedBy={board.updatedBy}
                                  memberships={board.memberships}
                                  templates={boardTemplates}
                                  boardName={board.name}
                                  isAdmin={isAdmin}
                                  mailTokens={board.mailTokens}
                                  mailTokenCount={board.mailTokenCount}
                                  mailServiceAvailable={mailServiceAvailable}
                                  mailServiceInboundEmail={mailServiceInboundEmail}
                                  isProjectManager={isProjectManager}
                                  canEdit={board.canEdit}
                                  isFetching={board.isFetching}
                                  boardMembershipId={board.boardMembershipId}
                                  hideCompletedLists={board.hideCompletedLists}
                                  onMembershipUpdate={onBoardMembershipUpdate}
                                  onUpdate={(data) => onBoardUpdate(board.id, data)}
                                  onExport={(data) => onBoardExport(board.id, data)}
                                  onFetch={() => onBoardFetch(board.id)}
                                  onDelete={() => onBoardDelete(board.id)}
                                  onActivitiesFetch={() => onActivitiesBoardFetch(board.id)}
                                  onMailTokenCreate={() => onMailTokenCreate(board.id)}
                                  onMailTokenUpdate={(mailTokenId) => onMailTokenUpdate(mailTokenId, board.id)}
                                  onMailTokenDelete={(mailTokenId) => onMailTokenDelete(mailTokenId)}
                                  onTemplateCreate={(data) => onBoardTemplateCreate(board.id, data)}
                                  onTemplateUpdate={onBoardTemplateUpdate}
                                  onTemplateDelete={onBoardTemplateDelete}
                                  position="right-start"
                                  offset={10}
                                  hideCloseButton
                                >
                                  <Button variant={ButtonVariant.Icon} title={t('common.editBoard', { context: 'title' })} className={s.hoverButton}>
                                    <Icon type={IconType.EllipsisVertical} size={IconSize.Size13} />
                                  </Button>
                                </BoardActionsPopup>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      );
    });

    return (
      <div className={s.wrapper}>
        {sidebarShown && <button type="button" className={s.mobileBackdrop} onClick={toggleSidebar} aria-label={t('common.hideSidebar')} />}
        <div className={clsx(s.sidebar, sidebarCompact && s.sidebarCompact, !sidebarShown && s.sidebarHidden)}>
          {!settingsOnly && (
            <div className={s.sidebarTop}>
              <div className={s.sidebarTopFilter}>
                <Filter defaultValue="" projects={projects} filteredProjects={filteredProjects} path={path} onChangeFilterQuery={onChangeFilterQuery} onFilterQueryClear={handleFilterQueryClear} />
              </div>
            </div>
          )}
          <div className={clsx(s.scrollable, gs.scrollableY)}>
            {settingsOnly && (
              <div>
                <div className={s.sidebarTitle}>
                  <Icon type={IconType.Settings} size={IconSize.Size16} className={s.sidebarTitleIcon} />
                  {t('common.settings')}
                </div>
                <div className={clsx(s.sidebarItem, path === Paths.SETTINGS_PROFILE && s.sidebarActive)}>
                  <Link to={Paths.SETTINGS_PROFILE}>
                    <Button variant={ButtonVariant.NoBackground} title={t('common.profile')} className={clsx(s.sidebarButton, s.sidebarButtonPadding)}>
                      <Icon type={IconType.User} size={IconSize.Size14} className={s.icon} />
                      {t('common.profile')}
                    </Button>
                  </Link>
                </div>
                <div className={clsx(s.sidebarItem, path === Paths.SETTINGS_PREFERENCES && s.sidebarActive)}>
                  <Link to={Paths.SETTINGS_PREFERENCES}>
                    <Button variant={ButtonVariant.NoBackground} title={t('common.preferences')} className={clsx(s.sidebarButton, s.sidebarButtonPadding)}>
                      <Icon type={IconType.Sliders} size={IconSize.Size14} className={s.icon} />
                      {t('common.preferences')}
                    </Button>
                  </Link>
                </div>
                <div className={clsx(s.sidebarItem, path === Paths.SETTINGS_ACCOUNT && s.sidebarActive)}>
                  <Link to={Paths.SETTINGS_ACCOUNT}>
                    <Button variant={ButtonVariant.NoBackground} title={t('common.account')} className={clsx(s.sidebarButton, s.sidebarButtonPadding)}>
                      <Icon type={IconType.AddressCard} size={IconSize.Size14} className={s.icon} />
                      {t('common.account')}
                    </Button>
                  </Link>
                </div>
                <div className={clsx(s.sidebarItem, path === Paths.SETTINGS_AUTHENTICATION && s.sidebarActive)}>
                  <Link to={Paths.SETTINGS_AUTHENTICATION}>
                    <Button variant={ButtonVariant.NoBackground} title={t('common.authentication')} className={clsx(s.sidebarButton, s.sidebarButtonPadding)}>
                      <Icon type={IconType.Key} size={IconSize.Size14} className={s.icon} />
                      {t('common.authentication')}
                    </Button>
                  </Link>
                </div>
                <div className={clsx(s.sidebarItem, path === Paths.SETTINGS_ABOUT && s.sidebarActive)}>
                  <Link to={Paths.SETTINGS_ABOUT}>
                    <Button variant={ButtonVariant.NoBackground} title={t('common.aboutShort')} className={clsx(s.sidebarButton, s.sidebarButtonPadding)}>
                      <Icon type={IconType.Info} size={IconSize.Size14} className={s.icon} />
                      {t('common.aboutShort')}
                    </Button>
                  </Link>
                </div>
              </div>
            )}
            {isAdmin && settingsOnly && (
              <div>
                <div className={s.sidebarTitle}>
                  <Icon type={IconType.Server} size={IconSize.Size16} className={s.sidebarTitleIcon} />
                  {t('common.instanceSettings')}
                </div>
                <div className={clsx(s.sidebarItem, path === Paths.SETTINGS_USERS && s.sidebarActive)}>
                  <Link to={Paths.SETTINGS_USERS}>
                    <Button variant={ButtonVariant.NoBackground} title={t('common.users')} className={clsx(s.sidebarButton, s.sidebarButtonPadding)}>
                      <Icon type={IconType.Users} size={IconSize.Size14} className={s.icon} />
                      {t('common.users')}
                      {usersNotificationCount > 0 && <span className={s.notification}>{usersNotificationCount}</span>}
                    </Button>
                  </Link>
                </div>
                <div className={clsx(s.sidebarItem, path === Paths.SETTINGS_INSTANCE && s.sidebarActive)}>
                  <Link to={Paths.SETTINGS_INSTANCE}>
                    <Button variant={ButtonVariant.NoBackground} title={t('common.settings')} className={clsx(s.sidebarButton, s.sidebarButtonPadding)}>
                      <Icon type={IconType.Settings} size={IconSize.Size14} className={s.icon} />
                      {t('common.settings')}
                      {instanceNotificationCount > 0 && <span className={s.notification}>{instanceNotificationCount}</span>}
                    </Button>
                  </Link>
                </div>
              </div>
            )}
            {!settingsOnly && <div>{projectsNode}</div>}
          </div>
          <div className={s.sidebarFooter}>
            {!settingsOnly && canAddProject && (
              <ProjectAddPopup defaultData={defaultData} isSubmitting={isSubmitting} onCreate={onProjectCreate} offset={16} position="right">
                <Button variant={ButtonVariant.Primary} title={t('common.addProject')} className={clsx(s.footerButton, s.footerButtonPrimary)}>
                  <Icon type={IconType.Plus} size={IconSize.Size13} className={s.footerButtonIcon} />
                  {t('common.addProject')}
                </Button>
              </ProjectAddPopup>
            )}
            {managedProjects.length > 0 && !settingsOnly && (
              <BoardAddPopup
                projects={managedProjects}
                projectId={currProjectId}
                isAdmin={isAdmin}
                templates={boardTemplates}
                onCreate={onBoardCreate}
                onTemplateUpdate={onBoardTemplateUpdate}
                onTemplateDelete={onBoardTemplateDelete}
                offset={16}
                position="right"
              >
                <Button variant={ButtonVariant.NoBackground} title={t('common.addBoard')} className={s.footerButton}>
                  <Icon type={IconType.Plus} size={IconSize.Size13} className={s.footerButtonIcon} />
                  {t('common.addBoard')}
                </Button>
              </BoardAddPopup>
            )}
            {settingsOnly && (
              <Link to={Paths.ROOT}>
                <Button variant={ButtonVariant.NoBackground} title={t('common.backToDashboard')} className={s.footerButton}>
                  <Icon type={IconType.ArrowLeftBig} size={IconSize.Size13} className={s.footerButtonIcon} />
                  {t('common.dashboard')}
                </Button>
              </Link>
            )}
          </div>
        </div>
        <div className={clsx(s.content, sidebarCompact && s.contentCompact)}>{children}</div>
      </div>
    );
  },
);

Sidebar.propTypes = {
  children: PropTypes.node.isRequired,
  settingsOnly: PropTypes.bool,
  path: PropTypes.string.isRequired,
  projects: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  filteredProjects: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  managedProjects: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  currProjectId: PropTypes.string,
  currBoardId: PropTypes.string,
  isAdmin: PropTypes.bool.isRequired,
  canAddProject: PropTypes.bool.isRequired,
  defaultData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  isSubmitting: PropTypes.bool.isRequired,
  filterQuery: PropTypes.string,
  filterTarget: PropTypes.string,
  sidebarCompact: PropTypes.bool.isRequired,
  instanceNotificationCount: PropTypes.number.isRequired,
  usersNotificationCount: PropTypes.number.isRequired,
  boardTemplates: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  mailServiceAvailable: PropTypes.bool.isRequired,
  mailServiceInboundEmail: PropTypes.string.isRequired,
  onProjectCreate: PropTypes.func.isRequired,
  onProjectUpdate: PropTypes.func.isRequired,
  onBoardCreate: PropTypes.func.isRequired,
  onBoardUpdate: PropTypes.func.isRequired,
  onBoardMove: PropTypes.func.isRequired,
  onBoardDelete: PropTypes.func.isRequired,
  onBoardExport: PropTypes.func.isRequired,
  onBoardFetch: PropTypes.func.isRequired,
  onBoardTemplateCreate: PropTypes.func.isRequired,
  onBoardTemplateUpdate: PropTypes.func.isRequired,
  onBoardTemplateDelete: PropTypes.func.isRequired,
  onChangeFilterQuery: PropTypes.func.isRequired,
  onProjectMembershipUpdate: PropTypes.func.isRequired,
  onActivitiesProjectFetch: PropTypes.func.isRequired,
  onActivitiesBoardFetch: PropTypes.func.isRequired,
  onMailTokenCreate: PropTypes.func.isRequired,
  onMailTokenUpdate: PropTypes.func.isRequired,
  onMailTokenDelete: PropTypes.func.isRequired,
  onBoardMembershipUpdate: PropTypes.func.isRequired,
};

Sidebar.defaultProps = {
  settingsOnly: false,
  currProjectId: undefined,
  currBoardId: undefined,
  filterQuery: undefined,
  filterTarget: undefined,
};

export default Sidebar;
